// Shared Gantt chart component - used by Planning and Execution views
import { useState, useMemo } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import type { ProjectPlan } from '../../types';
import type { ExecutionPlan } from '../../types';

export interface GanttItem {
  id: string;
  label: string;
  startDate: Date;
  endDate: Date;
  level: number; // 0 = Phase, 1 = Task
  phaseId?: number;
  taskId?: number;
  color: string;
}

export interface GanttChartViewProps {
  ganttItems: GanttItem[];
  projectStartDate: Date;
  projectEndDate: Date | null;
  title?: string;
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const date = new Date(value + (value.includes('T') ? '' : 'T00:00:00'));
    return isNaN(date.getTime()) ? null : date;
  }
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  return null;
}

/** Build Gantt data from ProjectPlan (planning view) */
export function projectPlanToGanttData(projectPlan: ProjectPlan): {
  ganttItems: GanttItem[];
  projectStartDate: Date;
  projectEndDate: Date | null;
} {
  const projectStartDate = parseDate(projectPlan.projectStartDate) ?? new Date();
  let projectEndDate: Date | null = null;
  const items: GanttItem[] = [];

  projectPlan.phases.forEach((phase) => {
    const phaseStart = parseDate(phase.startDate);
    const phaseEnd = parseDate(phase.endDate);
    if (phaseEnd && (!projectEndDate || phaseEnd > projectEndDate)) projectEndDate = phaseEnd;
    if (!phaseStart || !phaseEnd) return;

    items.push({
      id: `phase-${phase.phaseId}`,
      label: phase.name,
      startDate: phaseStart,
      endDate: phaseEnd,
      level: 0,
      phaseId: phase.phaseId,
      color: '#2196f3',
    });

    phase.tasks.forEach((task) => {
      const taskStart = parseDate(task.startDate);
      const taskEnd = parseDate(task.endDate);
      if (!taskStart || !taskEnd) return;
      items.push({
        id: `task-${task.taskId}`,
        label: task.name,
        startDate: taskStart,
        endDate: taskEnd,
        level: 1,
        phaseId: phase.phaseId,
        taskId: task.taskId,
        color: '#9c27b0',
      });
    });
  });

  return { ganttItems: items, projectStartDate, projectEndDate };
}

/** Build Gantt data from ExecutionPlan (execution view) using contracted dates */
export function executionPlanToGanttData(executionPlan: ExecutionPlan): {
  ganttItems: GanttItem[];
  projectStartDate: Date;
  projectEndDate: Date | null;
} {
  const projectStartDate = parseDate(executionPlan.projectStartDate) ?? new Date();
  let projectEndDate: Date | null = null;
  const items: GanttItem[] = [];

  executionPlan.phases.forEach((phase) => {
    const phaseStart = parseDate(phase.contractedStartDate);
    const phaseEnd = parseDate(phase.contractedEndDate);
    if (phaseEnd && (!projectEndDate || phaseEnd > projectEndDate)) projectEndDate = phaseEnd;
    if (!phaseStart || !phaseEnd) return;

    items.push({
      id: `phase-${phase.phaseId}`,
      label: phase.name,
      startDate: phaseStart,
      endDate: phaseEnd,
      level: 0,
      phaseId: phase.phaseId,
      color: '#2196f3',
    });

    phase.tasks.forEach((task) => {
      const taskStart = parseDate(task.contractedStartDate);
      const taskEnd = parseDate(task.contractedEndDate);
      if (!taskStart || !taskEnd) return;
      items.push({
        id: `task-${task.taskId}`,
        label: task.name,
        startDate: taskStart,
        endDate: taskEnd,
        level: 1,
        phaseId: phase.phaseId,
        taskId: task.taskId,
        color: '#9c27b0',
      });
    });
  });

  return { ganttItems: items, projectStartDate, projectEndDate };
}

export default function GanttChartView({
  ganttItems,
  projectStartDate,
  projectEndDate,
  title = 'Gantt Chart View',
}: GanttChartViewProps) {
  const [expanded, setExpanded] = useState(false);

  const timelineStart = useMemo(() => {
    if (!projectEndDate) return projectStartDate;
    return startOfMonth(projectStartDate);
  }, [projectStartDate, projectEndDate]);

  const timelineEnd = useMemo(() => {
    if (!projectEndDate) return projectStartDate;
    return endOfMonth(projectEndDate);
  }, [projectEndDate, projectStartDate]);

  const totalDays = useMemo(() => {
    if (!projectEndDate) return 0;
    return Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));
  }, [timelineStart, timelineEnd, projectEndDate]);

  const getBarStyle = (item: GanttItem) => {
    if (
      !item.startDate ||
      !item.endDate ||
      isNaN(item.startDate.getTime()) ||
      isNaN(item.endDate.getTime()) ||
      isNaN(timelineStart.getTime())
    ) {
      return { left: '0%', width: '0%' };
    }
    const startOffset = Math.ceil((item.startDate.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((item.endDate.getTime() - item.startDate.getTime()) / (1000 * 60 * 60 * 24));
    const percentageStart = (startOffset / totalDays) * 100;
    const percentageWidth = (duration / totalDays) * 100;
    return {
      left: `${percentageStart}%`,
      width: `${Math.max(percentageWidth, 0.5)}%`,
    };
  };

  const monthMarkers = useMemo(() => {
    const markers: { date: Date; label: string; position: number }[] = [];
    const months = eachMonthOfInterval({ start: timelineStart, end: timelineEnd });
    months.forEach((month) => {
      const offset = Math.ceil((month.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));
      const position = (offset / totalDays) * 100;
      markers.push({ date: month, label: format(month, 'MMM yyyy'), position });
    });
    return markers;
  }, [timelineStart, timelineEnd, totalDays, projectEndDate]);

  const phaseItems = useMemo(
    () => ganttItems.filter((i) => i.level === 0).sort((a, b) => (a.phaseId ?? 0) - (b.phaseId ?? 0)),
    [ganttItems]
  );

  if (!projectEndDate || ganttItems.length === 0) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)} sx={{ mt: 3 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box
            sx={{
              position: 'relative',
              height: 60,
              borderBottom: '2px solid #e0e0e0',
              mb: 2,
              minWidth: `${Math.max(totalDays * 2, 800)}px`,
              backgroundColor: '#fafafa',
            }}
          >
            {monthMarkers.map((marker, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: `${marker.position}%`,
                  top: 0,
                  height: '100%',
                  borderLeft: '1px solid #e0e0e0',
                  pl: 1,
                  pt: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  {marker.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ minWidth: `${Math.max(totalDays * 2, 800)}px` }}>
            {phaseItems.map((phaseItem) => {
              const phaseTasks = ganttItems.filter(
                (item) => item.phaseId === phaseItem.phaseId && item.level === 1
              );
              const phaseBarStyle = getBarStyle(phaseItem);

              return (
                <Box key={phaseItem.id} sx={{ mb: 2 }}>
                  <Box sx={{ position: 'relative', height: 40, mb: 1 }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 200,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        pr: 2,
                        backgroundColor: '#f5f5f5',
                        borderRight: '1px solid #e0e0e0',
                        zIndex: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {phaseItem.label}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', ml: '200px', height: '100%', borderBottom: '1px solid #e0e0e0' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          ...phaseBarStyle,
                          top: '10%',
                          height: '80%',
                          backgroundColor: phaseItem.color,
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 'medium',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.9, boxShadow: '0 4px 8px rgba(0,0,0,0.3)' },
                        }}
                        title={
                          phaseItem.startDate && phaseItem.endDate
                            ? `${format(phaseItem.startDate, 'MMM dd, yyyy')} - ${format(phaseItem.endDate, 'MMM dd, yyyy')}`
                            : 'Date range not available'
                        }
                      >
                        {phaseItem.startDate && phaseItem.endDate
                          ? `${Math.ceil((phaseItem.endDate.getTime() - phaseItem.startDate.getTime()) / (1000 * 60 * 60 * 24))}d`
                          : 'N/A'}
                      </Box>
                    </Box>
                  </Box>

                  {phaseTasks.map((task) => {
                    const taskBarStyle = getBarStyle(task);
                    return (
                      <Box key={task.id} sx={{ position: 'relative', height: 32, mb: 0.5 }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: 200,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            pl: 3,
                            pr: 2,
                            backgroundColor: '#fafafa',
                            borderRight: '1px solid #e0e0e0',
                            zIndex: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: '#666',
                            }}
                          >
                            {task.label}
                          </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', ml: '200px', height: '100%', borderBottom: '1px solid #f0f0f0' }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              ...taskBarStyle,
                              top: '15%',
                              height: '70%',
                              backgroundColor: task.color,
                              borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 'medium',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.9, boxShadow: '0 2px 6px rgba(0,0,0,0.25)' },
                            }}
                            title={
                              task.startDate && task.endDate
                                ? `${format(task.startDate, 'MMM dd, yyyy')} - ${format(task.endDate, 'MMM dd, yyyy')}`
                                : 'Date range not available'
                            }
                          >
                            {task.startDate && task.endDate
                              ? `${Math.ceil((task.endDate.getTime() - task.startDate.getTime()) / (1000 * 60 * 60 * 24))}d`
                              : 'N/A'}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
